import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from "@mui/material";
import axios from "axios";
import CustomButton from "../../components/CustomButton/Button";
import { REDIS_GET, REDIS_SET } from "../../utils/url";
import Loader from "../../components/loader/Loader";

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

const FEATURE_KEY = "EnableNonPayingBusinessRestrictedAccessFeature";

const FeatureSettings: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [initialValue, setInitialValue] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const isDirty = enabled !== initialValue;

  const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `bearer ${localStorage.getItem("accessToken") || ""}`
  });

  const fetchCurrentValue = async () => {
    setFetching(true);
    setError("");
    try {
      const response = await axios.post(
        `${apiEndpoint}${REDIS_GET}`,
        { key: FEATURE_KEY },
        { headers: getAuthHeaders() }
      );
      const value =
        response.data?.value ?? response.data?.Value ?? response.data;
      const isEnabled = value === "1" || value === 1 || value === true;
      setEnabled(isEnabled);
      setInitialValue(isEnabled);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError("You do not have permission to access feature flags.");
      } else if (status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError("Unable to load feature flags. Please try again later.");
      }
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchCurrentValue();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await axios.post(
        `${apiEndpoint}${REDIS_SET}`,
        {
          key: FEATURE_KEY,
          value: enabled ? "1" : "0"
        },
        { headers: getAuthHeaders() }
      );
      setSuccess("Feature flag saved successfully.");
      setInitialValue(enabled);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError("You do not have permission to update feature flags.");
      } else if (status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError("Unable to save feature flag. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <Loader />;
  }

  return (
    <Paper sx={{ p: 3 }}>
      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {success && (
        <Box mb={2}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 1
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Non-Paying Business Restricted Access
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={enabled ? "Enabled" : "Disabled"}
            labelPlacement="start"
          />
          <CustomButton callBack={handleSave} disabled={loading || !isDirty}>
            {loading ? "Saving..." : "Save"}
          </CustomButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default FeatureSettings;
